import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React from 'react';
import { ColumnId } from '@sales-portal/domain/pricing/screens/management/types';
import { CostChangeLookbackMode } from '@domain/configuration/types';
import { formatSummaryDate } from '@/utils/date';
import { isDeltaColumn } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { PricingTableHeaderLabel } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header-label';

interface Props {
  columnId: ColumnId;
  costChangeLookbackMode?: CostChangeLookbackMode;
  costChangeWeekAnchorDay?: number;
  withTooltip?: boolean;
}

export const HEADER_LABEL_KEYS: Record<ColumnId, string | null> = {
  [ColumnId.BookmarkedDate]: 'pricing.table.header.bookmarked_date',
  [ColumnId.Category]: 'pricing.table.header.category',
  [ColumnId.Checkbox]: null,
  [ColumnId.CostDelta]: 'pricing.table.header.cost_delta',
  [ColumnId.CurrentCost]: 'pricing.table.header.current_cost',
  [ColumnId.CurrentGrossMargin]: 'pricing.table.header.current_gross_margin',
  [ColumnId.CurrentPrice]: 'pricing.table.header.current_price',
  [ColumnId.EditsGrossProfitImpactPerOrder]:
    'pricing.table.header.edits_impact_per_order',
  [ColumnId.GrossMarginPercentageDelta]:
    'pricing.table.header.gross_margin_percentage_delta',
  [ColumnId.GrossProfit]: 'pricing.table.header.gross_profit',
  [ColumnId.Id]: 'pricing.table.header.id',
  [ColumnId.LastCost]: 'pricing.table.header.last_cost',
  [ColumnId.LastMargin]: 'pricing.table.header.last_margin',
  [ColumnId.LastPrice]: 'pricing.table.header.last_price',
  [ColumnId.Name]: 'pricing.table.header.name',
  [ColumnId.PeerRange]: 'pricing.table.header.peer_range',
  [ColumnId.PriceDelta]: 'pricing.table.header.price_delta',
  [ColumnId.UOM]: 'pricing.table.header.uom',
  [ColumnId.VolumePerOrder]: 'pricing.table.header.volume_per_order',
  [ColumnId.WeeklyCostChangeGrossProfitImpact]:
    'pricing.table.header.weekly_cost_change_gross_profit_impact',
  [ColumnId.WeeklyEditsGrossProfitImpact]:
    'pricing.table.header.weekly_edits_impact',
  [ColumnId.WeeklyNetGrossProfitImpact]:
    'pricing.table.header.weekly_net_gross_profit_impact',
  [ColumnId.WeeklyPurchaseQuantities]:
    'pricing.table.header.weekly_purchase_quantities',
  [ColumnId.WeeklyVolume]: 'pricing.table.header.weekly_volume',
};

const getCompletedWeekRange = (
  anchorDay: number,
): { end: string; start: string } => {
  const today = moment().tz(moment.tz.guess());
  const currentDay = today.day();

  const daysSinceAnchor =
    currentDay >= anchorDay
      ? currentDay - anchorDay
      : 7 - (anchorDay - currentDay);

  const end = today.clone().subtract(daysSinceAnchor, 'days');
  const start = end.clone().subtract(7, 'days');

  return {
    end: formatSummaryDate(end),
    start: formatSummaryDate(start),
  };
};

export const getHeaderLabel = (columnId: ColumnId): string => {
  const key = HEADER_LABEL_KEYS[columnId];
  return key ? i18n.t(key) : '';
};

const getTooltipText = (
  columnId: ColumnId,
  costChangeLookbackMode?: CostChangeLookbackMode,
  costChangeWeekAnchorDay?: number,
): string => {
  const baseKey = HEADER_LABEL_KEYS[columnId];
  if (!baseKey) {
    return '';
  }

  if (!isDeltaColumn(columnId)) {
    return i18n.t(`${baseKey}.tooltip_text`);
  }

  switch (costChangeLookbackMode) {
    case CostChangeLookbackMode.COMPLETED_WEEK: {
      const { start, end } = getCompletedWeekRange(
        costChangeWeekAnchorDay ?? 0,
      );
      return i18n.t(`${baseKey}.tooltip_text.completed_week`, {
        endDate: end,
        startDate: start,
      });
    }
    case CostChangeLookbackMode.LAST_CHANGE:
      return i18n.t(`${baseKey}.tooltip_text.last_change`);
    case CostChangeLookbackMode.ROLLING_WINDOW:
    default:
      return i18n.t(`${baseKey}.tooltip_text`);
  }
};

export const PricingTableHeader: React.FC<Props> = ({
  columnId,
  costChangeLookbackMode,
  costChangeWeekAnchorDay,
  withTooltip = false,
}) => (
  <PricingTableHeaderLabel
    label={getHeaderLabel(columnId)}
    tooltipText={
      withTooltip
        ? getTooltipText(
            columnId,
            costChangeLookbackMode,
            costChangeWeekAnchorDay,
          )
        : undefined
    }
  />
);
